import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getProduct } from '../services/api';
import { useEcommerceTracking } from '../hooks/useEcommerceTracking';
import SEO from '../components/SEO';
import './ProductPage.css';

function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const { trackViewItem, trackAddToCart } = useEcommerceTracking();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        // Decode URL parameter to handle special characters like ®
        const decodedId = decodeURIComponent(id);
        const productData = await getProduct(decodedId);
        setProduct(productData);
        setLoading(false);

        // Track product view
        if (productData) {
          trackViewItem(productData);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, trackViewItem]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!product) {
    return <div className="error">Product not found</div>;
  }

  const hasDescription = product.description && product.description.trim() !== '';
  const hasShortDescription = product.short_description && product.short_description.trim() !== '';
  const hasAttributes = product.attributes && product.attributes.length > 0;

  // Extract product-specific information for fallback content
  const getAttributeValue = (attrName) => {
    const attr = product.attributes?.find(a => a.name.toLowerCase().includes(attrName.toLowerCase()));
    return attr ? attr.value.trim().replace(/\r/g, '') : null;
  };

  const manufacturer = getAttributeValue('manufacturer');
  const brand = getAttributeValue('brand');
  const productCategory = getAttributeValue('product category') || getAttributeValue('category');
  const weight = getAttributeValue('weight');

  // Generate product-specific short description
  const generateShortDescription = () => {
    const parts = [];
    if (manufacturer || brand) {
      const mfg = brand || manufacturer;
      parts.push(`${mfg} ${product.name.toLowerCase()}`);
    } else {
      parts.push(`Professional-grade ${product.name.toLowerCase()}`);
    }

    if (productCategory) {
      parts.push(`in the ${productCategory.toLowerCase()} category`);
    }

    parts.push('from verified industrial suppliers. Contact us for detailed specifications, pricing, and availability.');

    return parts.join(' ');
  };

  // Generate product-specific features based on attributes
  const generateKeyFeatures = () => {
    const features = [];

    if (manufacturer) {
      features.push(`Manufactured by ${manufacturer}, a trusted name in industrial equipment`);
    }
    if (brand) {
      features.push(`Genuine ${brand} brand product with full manufacturer support`);
    }
    if (weight) {
      features.push(`Shipping weight: ${weight} - suitable for standard freight options`);
    }
    if (productCategory) {
      features.push(`${productCategory} - designed for industrial applications`);
    }

    // Add generic features if we don't have enough specific ones
    const genericFeatures = [
      'Meets or exceeds ANSI and ISO industry specifications',
      'Backed by manufacturer warranty and technical support',
      'Available for immediate shipping or custom order',
      'Competitive pricing with volume discounts available',
      'Professional-grade construction for commercial use'
    ];

    while (features.length < 5) {
      features.push(genericFeatures[features.length - (features.length > 0 && manufacturer ? 1 : 0)]);
    }

    return features.slice(0, 5);
  };

  // Generate SEO-friendly description
  const seoDescription = hasShortDescription
    ? product.short_description.replace(/<[^>]*>/g, '').substring(0, 160)
    : generateShortDescription().substring(0, 160);

  const seoImage = product.images && product.images.length > 0
    ? product.images[0].src
    : '/favicon.ico';

  return (
    <div className="product-page">
      <SEO
        title={`${product.name} | Industrial Supply`}
        description={seoDescription}
        image={seoImage}
        type="product"
        product={product}
      />
      <div className="product-container">
        <div className="product-images">
          <div className="main-image">
            {product.images && product.images.length > 0 ? (
              <img src={product.images[selectedImage].src} alt={product.name} />
            ) : (
              <div className="no-image">No Image Available</div>
            )}
          </div>
          {product.images && product.images.length > 1 && (
            <div className="image-thumbnails">
              {product.images.map((image, index) => (
                <img
                  key={index}
                  src={image.src}
                  alt={`${product.name} ${index + 1}`}
                  className={selectedImage === index ? 'active' : ''}
                  onClick={() => setSelectedImage(index)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="product-details">
          <h1>{product.name}</h1>

          {product.sku && <p className="sku">SKU: {product.sku}</p>}

          <div className="product-price">
            {product.sale_price ? (
              <>
                <span className="sale-price">${product.sale_price}</span>
                <span className="regular-price">${product.regular_price}</span>
              </>
            ) : product.regular_price ? (
              <span className="price">${product.regular_price}</span>
            ) : (
              <span className="price">Request Quote</span>
            )}
          </div>

          {hasShortDescription ? (
            <div className="short-description" dangerouslySetInnerHTML={{ __html: product.short_description }} />
          ) : (
            <div className="short-description">
              <p>{generateShortDescription()}</p>
            </div>
          )}

          <div className="product-features">
            <div className="feature-badge">✓ Quality Certified</div>
            <div className="feature-badge">✓ Fast Shipping</div>
            <div className="feature-badge">✓ Expert Support</div>
          </div>

          <div className="product-actions">
            <button className="add-to-cart" onClick={() => trackAddToCart(product, 1)}>Request Quote</button>
            <button className="contact-seller">Contact Supplier</button>
          </div>
        </div>
      </div>

      {/* Product Information Tabs */}
      <div className="product-info-section">
        <div className="container">
          {hasDescription ? (
            <div className="description">
              <h2>Product Description</h2>
              <div dangerouslySetInnerHTML={{ __html: product.description }} />
            </div>
          ) : (
            <div className="description">
              <h2>Product Overview</h2>
              <p>
                {manufacturer || brand ? (
                  <>
                    The {product.name} from {brand || manufacturer} is a professional-grade industrial product
                    designed for commercial and manufacturing applications.
                  </>
                ) : (
                  <>
                    This {product.name} is a professional-grade industrial product designed for commercial
                    and manufacturing applications.
                  </>
                )}
                {productCategory && ` As a ${productCategory.toLowerCase()}, it`} meets or exceeds industry
                standards for quality and performance in demanding operational environments.
              </p>

              <h3>Key Features</h3>
              <ul className="feature-list">
                {generateKeyFeatures().map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>

              <h3>Applications</h3>
              <p>
                {productCategory ? (
                  <>
                    This {productCategory.toLowerCase()} is suitable for use in manufacturing facilities,
                    processing plants, maintenance operations, and commercial installations.
                  </>
                ) : (
                  <>
                    Suitable for use in manufacturing facilities, processing plants, maintenance operations,
                    and commercial installations.
                  </>
                )}
                {' '}Ideal for industries including automotive, aerospace, food processing, chemical processing,
                and general manufacturing.
              </p>

              <h3>Quality Assurance</h3>
              <p>
                {manufacturer || brand ? (
                  <>
                    All {brand || manufacturer} products are sourced from authorized distributors and undergo
                    quality inspection.
                  </>
                ) : (
                  <>
                    All products are sourced from verified suppliers and undergo quality inspection.
                  </>
                )}
                {' '}We ensure compliance with relevant safety standards and industry certifications for peace of mind.
              </p>
            </div>
          )}

          {hasAttributes && (
            <div className="attributes">
              <h2>Technical Specifications</h2>
              <table>
                <tbody>
                  {product.attributes.map((attr, index) => (
                    <tr key={index}>
                      <th>{attr.name}</th>
                      <td>{attr.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Additional Information Section */}
          <div className="additional-info">
            <h2>Additional Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <h3>Shipping & Delivery</h3>
                <p>Standard and expedited shipping options available. Most orders ship within 1-3 business days.
                Contact us for freight shipping on bulk orders.</p>
              </div>
              <div className="info-item">
                <h3>Returns & Warranty</h3>
                <p>Products are covered by manufacturer warranty. Returns accepted within 30 days for
                unused items in original packaging. Defective items replaced promptly.</p>
              </div>
              <div className="info-item">
                <h3>Technical Support</h3>
                <p>Our technical team is available to assist with product selection, specifications,
                installation guidance, and troubleshooting. Call 727-777-7525 for support.</p>
              </div>
              <div className="info-item">
                <h3>Volume Pricing</h3>
                <p>Competitive pricing available for bulk orders. Contact our sales team for custom
                quotes on large quantities and recurring orders.</p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="product-cta">
            <h2>Need Help With This Product?</h2>
            <p>Our industrial equipment specialists are ready to assist with specifications,
            pricing, and order fulfillment.</p>
            <div className="cta-buttons">
              <button className="cta-btn primary">Request Detailed Quote</button>
              <a href="tel:727-777-7525" className="cta-btn secondary">Call: 727-777-7525</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductPage;
